import React, { useEffect, useMemo, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Button from "@mui/material/Button";
import axios from "axios";

// Soft UI Dashboard components
import CloseIcon from "@mui/icons-material/Close";

// Soft UI Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import Loading from "components/Loading";

// Soft UI Dashboard layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import RecruiterTeachersDialog from "components/RecruiterTeachersDialog";
import AccountStatusTabs from "components/AccountStatusTabs";

import { useFormik } from "formik";
import Swal from "sweetalert2";
import api from "services/api";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { isAdmin, isSuperAdmin } from "assistant/userRole";
import { getStoredUser } from "utils/userProfile";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import {
  accountCapability,
  accountIsActive,
  apiErrorMessage,
  getManagedRecruiters,
  setManagedRecruiterStatus,
} from "services/accountManagementService";

const API = {
  list: `${apiurl}Recruiters/list`,
  addEdit: `${apiurl}Recruiters/addedit`,
  delete: `${apiurl}Recruiters/delete`,
};

// Keep recruiter compatibility requests isolated so an endpoint-specific 401/403
// never clears an otherwise valid web-panel session.
const recruiterCompatibilityApi = axios.create();

function legacyRecruiterItems(data) {
  const payload = data?.data ?? data?.Data ?? data;
  if (Array.isArray(payload)) return payload;
  const items = payload?.items ?? payload?.Items ?? payload?.recruiters ?? payload?.Recruiters;
  return Array.isArray(items) ? items : [];
}

function recruiterValidationMessage(error, fallback) {
  const data = error?.response?.data;
  const errors = data?.errors ?? data?.Errors;
  if (errors && typeof errors === "object") {
    const details = Object.entries(errors)
      .flatMap(([field, messages]) =>
        (Array.isArray(messages) ? messages : [messages])
          .filter(Boolean)
          .map((message) => `${field}: ${message}`)
      )
      .join(" · ");
    if (details) return details;
  }
  return apiErrorMessage(error, fallback);
}

const recruiterStatWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 112,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(102, 126, 234, 0.07) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "rgba(102, 126, 234, 0.28)",
      boxShadow: "0 16px 42px rgba(102, 126, 234, 0.12)",
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
  },
};

const formFieldLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

function Recruiters() {
  const [recruiters, setRecruiters] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [teachersOpen, setTeachersOpen] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const rowsPerPage = 25;
  const { t } = useTranslate();
  const currentUser = useMemo(() => getStoredUser(), []);
  const superAdminView = isSuperAdmin(currentUser);
  const adminView = isAdmin(currentUser);

  const token = useMemo(() => localStorage.getItem("tk446655440000"), []);
  const authHeader = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(process.env.REACT_APP_API_KEY ? { "X-ApiKey": process.env.REACT_APP_API_KEY } : {}),
    }),
    [token]
  );

  const fetchRecruiters = async (requestedPage = page, requestedStatus = statusFilter) => {
    setLoading(true);
    setError("");
    try {
      let result;
      if (superAdminView) {
        result = await getManagedRecruiters({
          status: requestedStatus,
          pageNumber: requestedPage,
          pageSize: rowsPerPage,
        });
      } else if (adminView) {
        const { data } = await recruiterCompatibilityApi.get(API.list, {
          headers: { ...authHeader },
          params: {
            pageNumber: requestedPage,
            pageSize: rowsPerPage,
            status: requestedStatus,
          },
        });
        const legacyItems = legacyRecruiterItems(data);
        const payload = data?.data ?? data?.Data ?? data;
        const pagination = payload?.pagination ?? payload?.Pagination;
        const serverPaged = pagination && !Array.isArray(payload);
        const visibleItems = requestedStatus === "inactive" ? [] : legacyItems;
        result = {
          items: serverPaged
            ? visibleItems
            : visibleItems.slice((requestedPage - 1) * rowsPerPage, requestedPage * rowsPerPage),
          pagination: {
            pageNumber: requestedPage,
            pageSize: rowsPerPage,
            totalRecords: serverPaged
              ? Number(pagination?.totalRecords ?? pagination?.TotalRecords ?? visibleItems.length)
              : visibleItems.length,
          },
          summary: { total: legacyItems.length, active: legacyItems.length, inactive: 0 },
        };
      } else {
        result = await getManagedRecruiters({
          status: requestedStatus,
          pageNumber: requestedPage,
          pageSize: rowsPerPage,
        });
      }
      setRecruiters(result.items);
      setSummary(result.summary);
      setTotalRecords(result.pagination.totalRecords);
    } catch (err) {
      console.error("Recruiters load error", {
        status: err?.response?.status,
        data: err?.response?.data,
        url: adminView ? API.list : `${apiurl}Recruiters/management`,
      });
      setError(
        recruiterValidationMessage(
          err,
          t("recruiters.load_error", "Failed to load recruiters.")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchRecruiters(1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const formik = useFormik({
    initialValues: {
      name: "",
      phoneNumber: "",
      whatsapp: "",
      userName: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = "Required";
      if (!values.phoneNumber) errors.phoneNumber = "Required";
      if (!editing && !values.userName) errors.userName = "Required";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("Id", editing?.id ? editing.id : 0);
        formData.append("Name", values.name);
        formData.append("PhoneNumber", values.phoneNumber);
        formData.append("Whatsapp", values.whatsapp);
        formData.append("WhastappNo", values.whatsapp);
        formData.append("UserName", editing?.id ? "" : values.userName);

        const response = await api.post(API.addEdit, formData, {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        });

        const isSuccess = response?.data?.success === true || response?.data === true;
        const responseCode = response?.data?.code ?? response?.data?.statusCode ?? response?.data?.status;
        const isConflict =
          response?.status === 409 ||
          Number(responseCode) === 409 ||
          String(responseCode || "").toLowerCase() === "conflict";

        if (isSuccess) {
          resetForm();
          setFormOpen(false);
          setEditing(null);
          await fetchRecruiters();
          Swal.fire({
            icon: "success",
            title: "Succes",
            text: "Recruteur enregistre avec succes.",
            timer: 2000,
            showConfirmButton: false,
          });
        } else if (isConflict) {
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Nom d'utilisateur deja pris.",
          });
        }
      } catch (err) {
        const statusCode = err?.response?.status;
        if (statusCode === 409) {
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Nom d'utilisateur deja pris.",
          });
        } else {
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Echec de l'enregistrement du recruteur.",
        });
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const openAdd = () => {
    setEditing(null);
    formik.resetForm();
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    formik.setValues({
      name: row?.name || "",
      phoneNumber: row?.phoneNumber || "",
      whatsapp: row?.whatsapp || row?.whastappNo || row?.whatsApp || "",
      userName: row?.userName || "",
    });
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    const confirmResult = await Swal.fire({
      title: "Etes-vous sur ?",
      text: "Cette action est irreversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (!confirmResult.isConfirmed) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", row.id);
      await api.post(API.delete, formData, {
        headers: {
          ...authHeader,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchRecruiters();
      Swal.fire({
        icon: "success",
        title: "Supprime",
        text: "Recruteur supprime avec succes.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Echec de la suppression du recruteur.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (row) => {
    const nextActive = !accountIsActive(row);
    const capability = nextActive ? "canActivate" : "canDeactivate";
    if (!superAdminView || !accountCapability(row, capability)) return;

    const result = await Swal.fire({
      title: nextActive
        ? t("recruiters.activate_confirm_title", "Activate this recruiter?")
        : t("recruiters.deactivate_confirm_title", "Deactivate this recruiter?"),
      text: nextActive
        ? t("recruiters.activate_confirm_text", "The recruiter will be able to enroll again.")
        : t("recruiters.deactivate_confirm_text", "The recruiter will immediately lose enrollment access."),
      input: "textarea",
      inputLabel: t("recruiters.status_reason", "Reason (optional)"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: nextActive ? figmaTokens.accent : "#dc2626",
      confirmButtonText: nextActive ? t("recruiters.activate", "Activate") : t("recruiters.deactivate", "Deactivate"),
      cancelButtonText: t("recruiters.cancel", "Cancel"),
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await setManagedRecruiterStatus(row.id ?? row.Id ?? row.recruiterId ?? row.RecruiterId, nextActive, result.value?.trim() || "");
      await fetchRecruiters(page, statusFilter);
      Swal.fire({
        icon: "success",
        title: nextActive ? t("recruiters.activate_success", "Recruiter activated") : t("recruiters.deactivate_success", "Recruiter deactivated"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("recruiters.status_error_title", "Status update failed"),
        text: apiErrorMessage(err, t("recruiters.status_error", "The recruiter status could not be changed.")),
      });
    } finally {
      setLoading(false);
    }
  };

  const openTeachers = (row) => {
    setSelectedRecruiter(row);
    setTeachersOpen(true);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const pagedRecruiters = recruiters;

  const renderFieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <SoftTypography variant="caption" color="error">
        {formik.errors[name]}
      </SoftTypography>
    ) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }}>
        <SoftBox mb={3}>
          <Card
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
              border: "none",
              boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)",
            }}
          >
            <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                p: { xs: 2.25, md: 3 },
              }}
            >
              <SoftBox sx={{ minWidth: 0, flex: "1 1 240px" }}>
                <SoftTypography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    color: figmaTokens.textMuted,
                    fontSize: "0.68rem",
                  }}
                >
                  {t("recruiters.page_kicker", "RECRUITMENT")}
                </SoftTypography>
                <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                  {t("recruiters.title", "Recruteurs")}
                </SoftTypography>
                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 520 }}>
                  {t(
                    "recruiters.page_hint",
                    "Manage recruiters, contact details, and linked teachers."
                  )}
                </SoftTypography>
              </SoftBox>
              <Button
                variant="contained"
                disableElevation
                onClick={openAdd}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.75,
                  py: 1.1,
                  borderRadius: "12px",
                  color: "#fff",
                  background: figmaTokens.gradientCSS,
                  boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                  "&:hover": {
                    filter: "brightness(1.06)",
                    boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                  },
                }}
              >
                {t("recruiters.add", "Ajouter un recruteur")}
              </Button>
            </SoftBox>
          </Card>
        </SoftBox>

        <SoftBox
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            mb: 3,
          }}
        >
          <SoftBox sx={recruiterStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("recruiters.stats.total", "Total recruiters") }}
              count={summary.total}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "info", component: "badge" }}
            />
          </SoftBox>
          <SoftBox sx={recruiterStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("recruiters.stats.active", "Active recruiters") }}
              count={summary.active}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "success", component: "check_circle" }}
            />
          </SoftBox>
          <SoftBox sx={recruiterStatWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("recruiters.stats.inactive", "Deactivated recruiters") }}
              count={summary.inactive}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "error", component: "block" }}
            />
          </SoftBox>
        </SoftBox>

        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
            bgcolor: figmaTokens.surface,
          }}
        >
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("recruiters.table_title", "Recruiter directory")}
            </SoftTypography>
            <AccountStatusTabs
              value={statusFilter}
              onChange={setStatusFilter}
              counts={{ all: summary.total, active: summary.active, inactive: summary.inactive }}
              labels={{
                all: t("recruiters.status_all", "All"),
                active: t("recruiters.status_active", "Active"),
                inactive: t("recruiters.status_inactive", "Deactivated"),
              }}
            />
          </SoftBox>

          {error && (
            <SoftBox px={2.5} pb={2} pt={1}>
              <Alert severity="error">{error}</Alert>
            </SoftBox>
          )}

          <SoftBox>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 0,
                border: "none",
                bgcolor: "transparent",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 720,
                  ...directoryTableHeadStickyOverridesSx,
                  ...directoryTableBodyCellDefaultsSx,
                }}
                aria-label={t("recruiters.title", "Recruteurs")}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("recruiters.name", "Nom")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("recruiters.phone", "Phone")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("recruiters.whatsapp", "WhatsApp")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("recruiters.username", "Username")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("recruiters.status", "Status")}
                    </TableCell>
                    <TableCell align="right" sx={directoryTableHeaderCellSx}>
                      {t("recruiters.actions", "Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRecruiters.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <SoftTypography variant="body2" color="text">
                          {t("recruiters.none", "No recruiters found.")}
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedRecruiters.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.85)" },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.name || "--"}</TableCell>
                      <TableCell>{row.phoneNumber || "--"}</TableCell>
                      <TableCell>{row.whatsapp || row.whastappNo || row.whatsApp || "--"}</TableCell>
                      <TableCell>{row.userName || "--"}</TableCell>
                      <TableCell>
                        <SoftBox
                          component="span"
                          sx={{
                            display: "inline-flex",
                            px: 1.1,
                            py: 0.45,
                            borderRadius: "999px",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: accountIsActive(row) ? "#047857" : "#64748b",
                            bgcolor: accountIsActive(row) ? "rgba(16, 185, 129, 0.12)" : "rgba(100, 116, 139, 0.12)",
                          }}
                        >
                          {accountIsActive(row) ? t("recruiters.status_active", "Active") : t("recruiters.status_inactive", "Deactivated")}
                        </SoftBox>
                      </TableCell>
                      <TableCell align="right">
                        <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(row)}
                            sx={{
                              color: figmaTokens.textSecondary,
                              "&:hover": { color: figmaTokens.accent, bgcolor: "rgba(102, 126, 234, 0.08)" },
                            }}
                          >
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openTeachers(row)}
                            sx={{
                              color: figmaTokens.textSecondary,
                              "&:hover": { color: figmaTokens.accent, bgcolor: "rgba(102, 126, 234, 0.08)" },
                            }}
                          >
                            <Icon fontSize="small">groups</Icon>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            sx={{
                              color: figmaTokens.textSecondary,
                              "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" },
                            }}
                          >
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                          {superAdminView && (accountCapability(row, "canActivate") || accountCapability(row, "canDeactivate")) && (
                            <IconButton
                              size="small"
                              onClick={() => handleStatusChange(row)}
                              title={accountIsActive(row) ? t("recruiters.deactivate", "Deactivate") : t("recruiters.activate", "Activate")}
                              sx={{
                                color: accountIsActive(row) ? "#dc2626" : "#059669",
                                "&:hover": { bgcolor: accountIsActive(row) ? "rgba(220, 38, 38, 0.08)" : "rgba(5, 150, 105, 0.08)" },
                              }}
                            >
                              <Icon fontSize="small">{accountIsActive(row) ? "block" : "check_circle"}</Icon>
                            </IconButton>
                          )}
                        </SoftBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalRecords > rowsPerPage && (
              <Stack spacing={2} alignItems="center" sx={{ py: 2.5, borderTop: `1px solid ${figmaTokens.cardHeaderRule}` }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => {
                    setPage(value);
                    fetchRecruiters(value, statusFilter);
                  }}
                  color="primary"
                  shape="rounded"
                />
              </Stack>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ p: 0 }}>
          <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
            <SoftTypography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: figmaTokens.textMuted,
                fontSize: "0.68rem",
              }}
            >
              {t("recruiters.page_kicker", "RECRUITMENT")}
            </SoftTypography>
            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {editing
                ? t("recruiters.edit", "Modifier un recruteur")
                : t("recruiters.add_modal", "Ajouter un recruteur")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 520 }}>
              {editing
                ? t("recruiters.form_hint_edit", "Update this recruiter's contact details.")
                : t("recruiters.form_hint_add", "Create a recruiter with login credentials.")}
            </SoftTypography>
            <IconButton
              aria-label="close"
              onClick={() => setFormOpen(false)}
              sx={{
                position: "absolute",
                right: 10,
                top: 14,
                color: figmaTokens.textSecondary,
                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 2, pt: 1 }}>
          <form id="recruiter-form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("recruiters.name", "Nom")} *
                </SoftTypography>
                <SoftInput
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {renderFieldError("name")}
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("recruiters.phone", "Téléphone")} *
                </SoftTypography>
                <SoftInput
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {renderFieldError("phoneNumber")}
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("recruiters.whatsapp", "WhatsApp")}
                </SoftTypography>
                <SoftInput
                  name="whatsapp"
                  value={formik.values.whatsapp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {renderFieldError("whatsapp")}
              </Grid>
              {!editing && (
                <Grid item xs={12} md={6}>
                  <SoftTypography component="label" sx={formFieldLabelSx}>
                    {t("recruiters.username", "Nom d'utilisateur")} *
                  </SoftTypography>
                  <SoftInput
                    name="userName"
                    value={formik.values.userName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {renderFieldError("userName")}
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
            bgcolor: figmaTokens.pageBg,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setFormOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.5,
              borderColor: figmaTokens.borderStrong,
              color: figmaTokens.textSecondary,
            }}
          >
            {t("recruiters.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            type="submit"
            form="recruiter-form"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2.75,
              py: 1,
              color: "#fff",
              background: figmaTokens.gradientCSS,
              boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
              "&:hover": {
                filter: "brightness(1.06)",
                boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
              },
            }}
          >
            {editing
              ? t("recruiters.update", "Mettre à jour")
              : t("recruiters.save", "Enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>

      <RecruiterTeachersDialog
        open={teachersOpen}
        onClose={() => setTeachersOpen(false)}
        recruiter={selectedRecruiter}
      />
    </DashboardLayout>
  );
}

export default Recruiters;
